import { Download, Loader2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { downloadJsonFile } from "@/lib/download-json";
import { cn } from "@/utils";
import type {
  StructuredOutputBackgroundRegressionBucket,
  StructuredOutputBackgroundRegressionJob,
  StructuredOutputBackgroundRegressionResultRow,
} from "@/types/structured-output-background-regression";

interface StructuredOutputBackgroundRegressionPanelProps {
  job: StructuredOutputBackgroundRegressionJob | null;
  polling: boolean;
  starting: boolean;
  error: string | null;
  onDismiss?: () => void;
}

function progressPercent(job: StructuredOutputBackgroundRegressionJob): number | null {
  if (job.progress.phase === "sampling" || job.progress.phase === "queued") {
    return null;
  }
  const total = job.progress.total_targets || 0;
  const done = job.progress.completed_targets + job.progress.failed_targets;
  if (total <= 0) return job.status === "completed" ? 100 : null;
  return Math.min(100, Math.round((done / total) * 100));
}

function tableHeadClassName(): string {
  return "bg-background text-[10px] uppercase tracking-wide text-muted-foreground";
}

function tableHeadCellClassName(align: "left" | "center" = "left"): string {
  return cn(
    "border-b bg-background px-2 py-1.5 font-medium",
    align === "center" && "text-center",
  );
}

function BucketTableHeader() {
  return (
    <thead className={tableHeadClassName()}>
      <tr>
        <th className={tableHeadCellClassName()}>Status</th>
        <th className={tableHeadCellClassName("center")}>Sampled</th>
        <th className={tableHeadCellClassName("center")}>Match</th>
        <th className={tableHeadCellClassName("center")}>Mismatch</th>
        <th className={tableHeadCellClassName("center")}>Errors</th>
      </tr>
    </thead>
  );
}

function exportFilename(job: StructuredOutputBackgroundRegressionJob): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  return `so-background-regression-${job.case_type}-${stamp}.json`;
}

function BucketRow({ bucket }: { bucket: StructuredOutputBackgroundRegressionBucket }) {
  const hasSamples = bucket.sampled > 0;
  return (
    <tr className={cn("border-b last:border-b-0", !hasSamples && "opacity-60")}>
      <td className="px-2 py-1.5 text-xs">{bucket.label}</td>
      <td className="px-2 py-1.5 text-center font-mono text-[11px]">
        {bucket.sampled}/{bucket.target}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-[11px] text-green-700 dark:text-green-300">
        {bucket.key_field_matches}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-[11px] text-amber-700 dark:text-amber-300">
        {bucket.key_field_mismatches}
      </td>
      <td className="px-2 py-1.5 text-center font-mono text-[11px] text-destructive">
        {bucket.errors}
      </td>
    </tr>
  );
}

function truncateMiddle(value: string, head = 8, tail = 4): string {
  const text = value.trim();
  if (text.length <= head + tail + 1) return text;
  return `${text.slice(0, head)}…${text.slice(-tail)}`;
}

function TruncatedCell({
  value,
  title,
  className,
  mono = false,
}: {
  value: string;
  title?: string;
  className?: string;
  mono?: boolean;
}) {
  return (
    <td
      className={cn(
        "max-w-0 px-2 py-1.5",
        mono ? "font-mono text-[11px]" : "text-[11px]",
        className,
      )}
      title={title ?? value}
    >
      <span className="block truncate">{value}</span>
    </td>
  );
}

function formatDiffValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function KeyFieldDiffCell({
  fieldKey,
  diff,
}: {
  fieldKey: string;
  diff?: { match?: boolean; current?: unknown; new?: unknown };
}) {
  const match = diff?.match ?? true;
  if (match) {
    return <span className="text-muted-foreground">match</span>;
  }

  const current = formatDiffValue(diff?.current);
  const next = formatDiffValue(diff?.new);
  const multiline =
    fieldKey === "success" ||
    fieldKey === "success_category" ||
    fieldKey === "pa_status";

  if (multiline) {
    return (
      <div className="space-y-0.5 break-words font-mono leading-snug">
        <div>{current}</div>
        <div className="pl-4">→ {next}</div>
      </div>
    );
  }

  return (
    <span className="block truncate" title={`${current} → ${next}`}>
      {current} → {next}
    </span>
  );
}

function ResultRow({ row, caseType }: { row: StructuredOutputBackgroundRegressionResultRow; caseType: string }) {
  const compareKeys =
    caseType === "bv"
      ? ["success", "success_category", "fail_reason"]
      : ["success", "pa_status", "fail_reason"];

  if (row.result === "error") {
    return (
      <tr className="border-b last:border-b-0">
        <TruncatedCell value={truncateMiddle(row.case_id)} mono title={row.case_id} />
        <TruncatedCell value={row.status_bucket} />
        <td className="px-2 py-1.5 text-xs text-destructive" colSpan={compareKeys.length}>
          <span className="block truncate" title={row.detail || "Error"}>
            {row.detail || "Error"}
          </span>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b last:border-b-0">
      <TruncatedCell value={truncateMiddle(row.case_id)} title={row.case_id} mono />
      <TruncatedCell value={row.status_bucket} />
      {compareKeys.map((key) => {
        const diff = row.key_field_diffs?.[key];
        const match = diff?.match ?? true;
        return (
          <td
            key={key}
            className={cn(
              "max-w-0 px-2 py-1.5 align-top text-[11px]",
              !match && "bg-amber-50/80 dark:bg-amber-950/20",
            )}
          >
            <KeyFieldDiffCell fieldKey={key} diff={diff} />
          </td>
        );
      })}
    </tr>
  );
}

export function StructuredOutputBackgroundRegressionPanel({
  job,
  polling,
  starting,
  error,
  onDismiss,
}: StructuredOutputBackgroundRegressionPanelProps) {
  if (!job && !error && !starting) return null;

  const compareKeys =
    job?.case_type === "bv"
      ? ["success", "success_category", "fail_reason"]
      : ["success", "pa_status", "fail_reason"];

  const mismatches =
    job?.results?.filter(
      (r) => r.result === "ok" && r.all_key_fields_match === false,
    ) ?? [];

  return (
    <section className="space-y-3 rounded-md border bg-muted/20 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Background regression by status</h2>
          <p className="text-xs text-muted-foreground">
            Samples recent cases per status bucket, re-runs Gemini with your selected
            prompt/schema, and compares key fields (
            {job?.case_type === "bv"
              ? "success, success_category, fail_reason"
              : "success, pa_status, fail_reason"}
            ).
          </p>
        </div>
        <div className="flex items-center gap-2">
          {job?.status === "completed" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => downloadJsonFile(exportFilename(job), job)}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Export
            </Button>
          ) : null}
          {onDismiss ? (
            <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
              Dismiss
            </Button>
          ) : null}
        </div>
      </div>

      {error ? (
        <p className="flex items-center gap-2 text-sm text-destructive">
          <XCircle className="h-4 w-4" />
          {error}
        </p>
      ) : null}

      {starting || polling || job ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {starting || polling || job?.status === "running" || job?.status === "queued" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
            ) : null}
            <Badge variant="outline">{job?.status ?? "starting"}</Badge>
            {job ? (
              <>
                <span>
                  Phase: <span className="font-medium">{job.progress.phase}</span>
                </span>
                <span>
                  Targets: {job.progress.completed_targets}/{job.progress.total_targets}
                  {job.progress.failed_targets > 0
                    ? ` (${job.progress.failed_targets} errors)`
                    : ""}
                </span>
              </>
            ) : null}
          </div>

          {job ? (
            <div className="h-2 overflow-hidden rounded-full bg-muted">
              {progressPercent(job) == null ? (
                <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
              ) : (
                <div
                  className="h-full bg-primary transition-all duration-300 ease-out"
                  style={{ width: `${progressPercent(job)}%` }}
                />
              )}
            </div>
          ) : null}

          {job?.summary ? (
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="rounded border px-2 py-1">
                Sampled: {job.summary.total_sampled}
              </span>
              <span className="rounded border border-green-200 bg-green-50 px-2 py-1 dark:border-green-900 dark:bg-green-950/30">
                Key field matches: {job.summary.key_field_matches}
              </span>
              <span className="rounded border border-amber-200 bg-amber-50 px-2 py-1 dark:border-amber-900 dark:bg-amber-950/30">
                Mismatches: {job.summary.key_field_mismatches}
              </span>
              {job.summary.key_field_match_rate != null ? (
                <span className="rounded border px-2 py-1">
                  Match rate: {(job.summary.key_field_match_rate * 100).toFixed(1)}%
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {job?.buckets?.length ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground">Status buckets</p>
          <div className="overflow-hidden rounded-md border bg-background">
            <table className="w-full table-fixed border-collapse text-left">
              <colgroup>
                <col className="w-[44%]" />
                <col className="w-[14%]" />
                <col className="w-[14%]" />
                <col className="w-[14%]" />
                <col className="w-[14%]" />
              </colgroup>
              <BucketTableHeader />
            </table>
            <ScrollArea className="h-48">
              <table className="w-full table-fixed border-collapse text-left">
                <colgroup>
                  <col className="w-[44%]" />
                  <col className="w-[14%]" />
                  <col className="w-[14%]" />
                  <col className="w-[14%]" />
                  <col className="w-[14%]" />
                </colgroup>
                <tbody>
                  {job.buckets
                    .filter((b) => b.sampled > 0 || b.target > 0)
                    .sort((a, b) => b.sampled - a.sampled || a.label.localeCompare(b.label))
                    .map((bucket) => (
                      <BucketRow key={bucket.bucket} bucket={bucket} />
                    ))}
                </tbody>
              </table>
            </ScrollArea>
          </div>
        </div>
      ) : null}

      {job?.status === "completed" && mismatches.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium text-amber-800 dark:text-amber-200">
            Key field mismatches ({mismatches.length})
          </p>
          <div className="overflow-hidden rounded-md border bg-background">
            <table className="w-full table-fixed border-collapse text-left">
              <colgroup>
                <col className="w-[14%]" />
                <col className="w-[18%]" />
                {compareKeys.map((key) => (
                  <col key={key} className="w-[23%]" />
                ))}
              </colgroup>
              <thead className={tableHeadClassName()}>
                <tr>
                  <th className={tableHeadCellClassName()}>Case</th>
                  <th className={tableHeadCellClassName()}>Bucket</th>
                  {compareKeys.map((key) => (
                    <th key={key} className={tableHeadCellClassName()}>
                      {key}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
            <ScrollArea className="h-56">
              <table className="w-full table-fixed border-collapse text-left">
                <colgroup>
                  <col className="w-[14%]" />
                  <col className="w-[18%]" />
                  {compareKeys.map((key) => (
                    <col key={key} className="w-[23%]" />
                  ))}
                </colgroup>
                <tbody>
                  {mismatches.map((row) => (
                    <ResultRow key={`${row.case_id}-${row.call_id}`} row={row} caseType={job.case_type} />
                  ))}
                </tbody>
              </table>
            </ScrollArea>
          </div>
        </div>
      ) : null}

      {job?.status === "failed" && job.error ? (
        <p className="text-sm text-destructive">{job.error}</p>
      ) : null}
    </section>
  );
}
